/**
 * Mapping validation, binding construction and mapping presets.
 *
 * A mapping is only usable when every required canonical bone is present, no source bone is used
 * twice, every named source bone actually exists, and the source hierarchy agrees with the
 * canonical one (plan D17). `buildBinding` then freezes the rest-space information the retarget
 * math needs, taken from the bind pose (D14).
 */

import {
  CANONICAL_BONE_IDS,
  CANONICAL_PARENT,
  REQUIRED_BONE_IDS,
  RIG_VERSION,
  identityQuat,
  newId,
  normalizeQuat,
  quatMultiply,
} from '../shared';
import type { CanonicalBoneId, MappingPreset, QuaternionTuple, RigBinding } from '../shared';
import { canonicalRestPositions, computeRestAlignment } from './restAlignment';
import { isDescendantOf, type SourceBoneInfo } from './sourceSkeleton';

export type BoneMapping = Partial<Record<CanonicalBoneId, string>>;

export interface MappingIssue {
  bone?: CanonicalBoneId;
  code: string;
  message: string;
}

export interface MappingValidationResult {
  ok: boolean;
  errors: MappingIssue[];
  warnings: MappingIssue[];
}

export const MAPPING_ERROR_CODES = {
  missingRequired: 'MISSING_REQUIRED_BONE',
  duplicateSource: 'DUPLICATE_SOURCE_BONE',
  unknownSource: 'UNKNOWN_SOURCE_BONE',
  hierarchy: 'HIERARCHY_MISMATCH',
} as const;

export const MAPPING_WARNING_CODES = {
  rootUnmapped: 'ROOT_UNMAPPED',
  unknownBoneId: 'UNKNOWN_CANONICAL_BONE',
} as const;

function indexBones(bones: readonly SourceBoneInfo[]): Map<string, SourceBoneInfo> {
  const byName = new Map<string, SourceBoneInfo>();
  for (const b of bones) if (!byName.has(b.name)) byName.set(b.name, b);
  return byName;
}

/** Nearest canonical ancestor of `bone` that the mapping actually resolves, skipping the rest. */
function nearestMappedCanonicalAncestor(
  bone: CanonicalBoneId,
  mapping: BoneMapping,
): CanonicalBoneId | null {
  let cursor = CANONICAL_PARENT[bone];
  while (cursor) {
    if (mapping[cursor]) return cursor;
    cursor = CANONICAL_PARENT[cursor];
  }
  return null;
}

/**
 * Validate a canonical→source bone mapping against a concrete skeleton.
 * Never throws; the UI renders `errors` and `warnings` directly.
 */
export function validateMapping(
  mapping: BoneMapping,
  bones: readonly SourceBoneInfo[],
): MappingValidationResult {
  const errors: MappingIssue[] = [];
  const warnings: MappingIssue[] = [];
  const byName = indexBones(bones);

  for (const key of Object.keys(mapping)) {
    if (!(CANONICAL_BONE_IDS as readonly string[]).includes(key)) {
      warnings.push({
        code: MAPPING_WARNING_CODES.unknownBoneId,
        message: `"${key}" is not a canonical bone id and will be ignored.`,
      });
    }
  }

  for (const bone of REQUIRED_BONE_IDS) {
    if (!mapping[bone]) {
      errors.push({
        bone,
        code: MAPPING_ERROR_CODES.missingRequired,
        message: `Required canonical bone "${bone}" is not mapped to any source bone.`,
      });
    }
  }

  if (!mapping.Root) {
    warnings.push({
      code: MAPPING_WARNING_CODES.rootUnmapped,
      message: 'No source bone is mapped to "Root". Root is optional; Root tracks are ignored.',
    });
  }

  // Existence + duplicates.
  const firstUser = new Map<string, CanonicalBoneId>();
  for (const bone of CANONICAL_BONE_IDS) {
    const sourceName = mapping[bone];
    if (!sourceName) continue;

    if (!byName.has(sourceName)) {
      errors.push({
        bone,
        code: MAPPING_ERROR_CODES.unknownSource,
        message: `"${bone}" is mapped to source bone "${sourceName}", which this skeleton does not have.`,
      });
      continue;
    }

    const owner = firstUser.get(sourceName);
    if (owner) {
      errors.push({
        bone,
        code: MAPPING_ERROR_CODES.duplicateSource,
        message: `Source bone "${sourceName}" is mapped to both "${owner}" and "${bone}". Each source bone may be used once.`,
      });
    } else {
      firstUser.set(sourceName, bone);
    }
  }

  // Hierarchy consistency (D17).
  for (const bone of CANONICAL_BONE_IDS) {
    const sourceName = mapping[bone];
    if (!sourceName || !byName.has(sourceName)) continue;

    const ancestor = nearestMappedCanonicalAncestor(bone, mapping);
    if (!ancestor) continue;
    const ancestorSource = mapping[ancestor];
    if (!ancestorSource || !byName.has(ancestorSource)) continue;
    if (ancestorSource === sourceName) continue; // already reported as a duplicate

    if (!isDescendantOf(byName, sourceName, ancestorSource)) {
      errors.push({
        bone,
        code: MAPPING_ERROR_CODES.hierarchy,
        message:
          `"${bone}" is mapped to "${sourceName}", which is not a descendant of "${ancestorSource}" ` +
          `(the source bone mapped to its canonical parent "${ancestor}").`,
      });
    }
  }

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * Freeze a mapping into a `RigBinding`.
 *
 * `restRotationOffsets[b] = worldCorrection * restWorldQuaternion(source)` — the `W_b` of plan
 * D5, always read from the bind pose. Bones the skeleton does not have are skipped rather than
 * emitted with a bogus offset; `validateMapping` is what reports them.
 *
 * `restAlignment[b] = E_b` (rigVersion `humanoid-v2`) is measured from the same bind pose and is
 * what lets a clip authored on a T-pose rig play on an A-pose one — see `rig/restAlignment.ts`.
 */
export function buildBinding(
  mapping: BoneMapping,
  bones: readonly SourceBoneInfo[],
  worldCorrection: QuaternionTuple = identityQuat(),
): RigBinding {
  const byName = indexBones(bones);
  const correction = normalizeQuat(worldCorrection);

  const canonicalToSourceBone: BoneMapping = {};
  const restRotationOffsets: Partial<Record<CanonicalBoneId, QuaternionTuple>> = {};

  for (const bone of CANONICAL_BONE_IDS) {
    const sourceName = mapping[bone];
    if (!sourceName) continue;
    const info = byName.get(sourceName);
    if (!info) continue;
    canonicalToSourceBone[bone] = sourceName;
    restRotationOffsets[bone] = normalizeQuat(
      quatMultiply(correction, info.restWorldQuaternion),
    );
  }

  const { alignment } = computeRestAlignment(
    canonicalRestPositions(canonicalToSourceBone, bones, correction),
  );

  return {
    rigVersion: RIG_VERSION,
    canonicalToSourceBone,
    restRotationOffsets,
    worldCorrection: correction,
    restAlignment: alignment,
  };
}

/** A reusable, model-independent mapping preset (persisted in IndexedDB by the editor). */
export function presetFromMapping(name: string, mapping: BoneMapping): MappingPreset {
  const canonicalToSourceBone: BoneMapping = {};
  for (const bone of CANONICAL_BONE_IDS) {
    const sourceName = mapping[bone];
    if (sourceName) canonicalToSourceBone[bone] = sourceName;
  }
  return {
    id: newId(),
    name,
    rigVersion: RIG_VERSION,
    canonicalToSourceBone,
    createdAt: Date.now(),
  };
}

/**
 * Apply a preset to a concrete skeleton. Only entries whose source bone exists here are kept;
 * the rest come back in `unresolved` so the mapping UI can highlight them.
 */
export function applyPreset(
  preset: MappingPreset,
  bones: readonly SourceBoneInfo[],
): { mapping: BoneMapping; unresolved: CanonicalBoneId[] } {
  const byName = indexBones(bones);
  const mapping: BoneMapping = {};
  const unresolved: CanonicalBoneId[] = [];

  for (const bone of CANONICAL_BONE_IDS) {
    const sourceName = preset.canonicalToSourceBone[bone];
    if (!sourceName) continue;
    if (byName.has(sourceName)) mapping[bone] = sourceName;
    else unresolved.push(bone);
  }

  return { mapping, unresolved };
}
